import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { MatomoTrackerService, TrackingCheckoutStep, TrackingService, trackingErrorCategory } from 'analytics';
import {
  EwalletConfirmationComponent,
  Web3ConfirmationComponent,
  PaypalConfirmationComponent,
  PaypalFailure,
  BankTransferConfirmationComponent,
  OrderService
} from 'credo';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';

import {
  FunnelService,
  HealthDeclarationQuestion,
  MembershipApplication,
  MembershipCategory,
  MembershipCheckout,
  MembershipCustomerInfo,
  MembershipTier,
  PendingOrder,
  PractitionerRenewalOffer,
  QuizPlan,
  SpecialUpgradeOffer,
  UpgradeOffer
} from '../../../services/funnel.service';
import { environment } from '../../../environments/environment';
import {
  ALL_BUNDLE_PLAN_CODES, BUNDLE_PLAN_CODES, FAMILY_BUNDLE_PLAN_CODES, consumeLoginReturn, currentReferral, isFamilyCode
} from '../../../services/funnel-intent';
import { ProductKey, durationLabel, isWeeklyCode, productKeyOf, productLabel, productName } from '../../../services/product-label';

const MATOMO_CATEGORY = 'Global Health Membership';
/** Tracking Engine V1 page id of the app checkout (docs/tracking/EVENT_CONTRACT_V1.md §2). */
const TRACK_PAGE = 'gh-checkout';
type Step = 'plan' | 'info' | 'notice' | 'declaration' | 'payment' | 'success' | 'upgrade' | 'special_upgrade'
  | 'practitioner_renewal';
/** The upgrade product to the DNA & Health SPECIAL (decision D of 2026-10-03) — a
 *  ?plan= request for it opens the ONE upgrade card, never the plan picker. */
const SPECIAL_UPGRADE_CODE = 'GH_DNA_HEALTH_SPECIAL_UPGRADE';
type StartMode = 'standard' | 'immediate';

/** The Practitioner Network plans — the club slot's upper tier. Listed here so the
 *  tier grouping still works against a backend that does not send `tier` yet. */
const PRACTITIONER_PLAN_CODES = ['GH_PRACTITIONER_WEEKLY', 'GH_PRACTITIONER_YEARLY'];

/** The club-slot plan codes — used to derive the category from a ?plan= param.
 *  Includes the retired rhythms so an old bookmarked ?plan= still resolves to the
 *  club slot rather than silently landing in the protection one. */
const CLUB_PLAN_CODES = [
  'GH_CLUB_WEEKLY', 'GH_CLUB_YEARLY', 'GH_CLUB', 'GH_CLUB_MONTHLY', 'GH_CLUB_SPECIAL',
  ...PRACTITIONER_PLAN_CODES,
  // The DNA & Health bundles (Website V2) contain a club membership year.
  ...ALL_BUNDLE_PLAN_CODES
];

interface CountryOption {
  code:   string;
  name:   string;
  states?: { code: string; name: string }[];
}

const PLAN_KEY_BY_CODE: Record<string, string> = {
  GH_CLUB_WEEKLY:         'club_weekly',
  GH_CLUB_YEARLY:         'club_yearly',
  GH_CLUB:                'club',
  GH_CLUB_MONTHLY:        'club_monthly',
  GH_CLUB_SPECIAL:        'club_special',
  GH_PRACTITIONER_WEEKLY: 'practitioner_weekly',
  GH_PRACTITIONER_YEARLY: 'practitioner_yearly',
  GH_DNA_HEALTH:          'dna_health',
  GH_DNA_HEALTH_SPECIAL:  'dna_health_special',
  GH_DNA_FAMILY_SPECIAL:  'dna_family_special',
  GH_SHIELD:              'shield',
  GH_EXECUTIVE:           'executive',
  GH_LEGACY:              'legacy'
};

/** One product as the plan step renders it: a single card whose plans are its
 *  duration options (customer decision 2026-10-02 — a duration is never a product). */
interface PlanTierGroup {
  tier:    MembershipTier;
  /** 'membership' | 'practitioner' — the buyer-facing product of this tier. */
  product: ProductKey;
  plans:   QuizPlan[];
}

/**
 * Wolfpack Global Health authenticated membership purchase wizard (Phase 2).
 * Steps: plan selection -> customer info -> payment (annual one-time, reusing
 * the shared order payment-confirmation components). Reached from the funnel
 * result page once the user is logged in. Phase 3 inserts the Health
 * Declaration steps before payment.
 */
@Component({
  selector: 'app-membership-apply-page',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonDirective, TranslatePipe,
    EwalletConfirmationComponent, Web3ConfirmationComponent,
    PaypalConfirmationComponent, BankTransferConfirmationComponent
  ],
  templateUrl: './membership-apply-page.component.html',
  styleUrls: ['./membership-apply-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MembershipApplyPageComponent {
  private funnel     = inject(FunnelService);
  private orders     = inject(OrderService);
  private http       = inject(HttpClient);
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private matomo     = inject(MatomoTrackerService);
  private tracking   = inject(TrackingService);
  private destroyRef = inject(DestroyRef);
  /** Last wizard position reported as checkout_step 'view' (step + whether an order exists). */
  private lastTrackedView = '';

  /** The `funnel.plans` locale node ({ shield: {...}, executive: {...}, legacy: {...} }). */
  private planContent = this.translate.translateObjectSignal('funnel.plans');

  planFeatures(code: string): string[] {
    const c = this.planContent() as Record<string, any> | undefined;
    return c?.[this.planKey(code)]?.features ?? [];
  }

  planField(code: string, field: string): string {
    const c = this.planContent() as Record<string, any> | undefined;
    return c?.[this.planKey(code)]?.[field] ?? '';
  }

  loading = signal(true);
  busy    = signal(false);
  error   = signal<string | null>(null);
  /** True once the application is finalized (declaration submitted/approved or purchased) — read-only. */
  locked  = signal(false);
  /** Required acknowledgement on the English-only language notice (Step 7). */
  noticeChecked = signal(false);

  step = signal<Step>('plan');
  app  = signal<MembershipApplication | null>(null);
  plans = signal<QuizPlan[]>([]);
  selectedPlan = signal<string | null>(null);
  /** Which membership slot this wizard pass is buying (club vs protection). */
  category = signal<MembershipCategory>('protection');
  /**
   * Only the plans of the category being bought, and of those only the ones the
   * server currently offers (`offered_plan_codes`). The server refuses anything
   * else in `set_plan`, so offering it here would only produce a dead card — e.g.
   * the Practitioner Network at full price for a member whose way there is the
   * difference-price upgrade. Falls back to the whole category when the backend
   * does not report the field (older backend).
   */
  visiblePlans = computed(() => {
    // Bundle mode (Website V2): the member came from a DNA & Health sales page, so
    // the plan step is that one bundle — no rhythm choice, no other cards.
    const bundle = this.requestedBundle();
    if (bundle) return this.plans().filter(p => p.code === bundle);
    // Otherwise a bundle is never offered here, even if the backend listed one.
    // A (former) Practitioner on a running SPECIAL buys the Practitioner Membership
    // through its own step (it must not replace the SPECIAL on the row) — never here.
    const viaRenewal = !!this.app()?.practitioner_renewal_offer;
    const ofCategory = this.plans().filter(p => p.category === this.category() && !this.isBundleCode(p.code) &&
                                                !(viaRenewal && PRACTITIONER_PLAN_CODES.includes(p.code)));
    const offered = this.app()?.offered_plan_codes ?? [];
    const narrowed = ofCategory.filter(p => offered.includes(p.code));
    // An empty result means the list says nothing about this category (an older
    // backend, or a club-only offer list on the protection slot) — fall back to the
    // whole category rather than showing a plan step with no plans on it. The
    // server still has the last word: `set_plan` refuses anything it did not offer.
    return narrowed.length ? narrowed : ofCategory;
  });

  /** The offered plans grouped by tier, club tier first. Empty groups are dropped. */
  planTierGroups = computed<PlanTierGroup[]>(() =>
    (['club', 'practitioner'] as MembershipTier[])
      .map(tier => ({
        tier,
        product: (tier === 'practitioner' ? 'practitioner' : 'membership') as ProductKey,
        // Weekly option first, then the year.
        plans: this.visiblePlans()
          .filter(p => this.planTier(p) === tier)
          .sort((a, b) => Number(isWeeklyCode(b.code)) - Number(isWeeklyCode(a.code)))
      }))
      .filter(group => group.plans.length > 0)
  );

  /** The plan step was skipped because the plan arrived preselected (website /
   *  direct checkout / dashboard card) — the summary then offers "change". */
  planStepSkipped = signal(false);

  /** "WolfPack Global Health Membership — 12 weeks" for what is being bought. */
  selectedLabel(): string {
    const code = this.selectedPlan();
    if (!code) return '';
    const plan = this.selectedPlanInfo();
    return productLabel(this.translate, code, this.isWeeklyPlan() ? this.weeks() : null, plan?.product_key);
  }

  /** True when one of this product's duration options is selected. */
  groupSelected(group: PlanTierGroup): boolean {
    return group.plans.some(p => p.code === this.selectedPlan());
  }

  /** Product name only (used where the duration is listed separately). */
  productNameOf(plan: QuizPlan | null | undefined): string {
    return plan ? productName(this.translate, plan.code, plan.product_key) : '';
  }

  /** A plan as a duration OPTION of its product: "Weekly — $9.99 per week …" / "1 year — $399". */
  planOptionLabel(plan: QuizPlan): string {
    const price = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      .format(Number(plan.price));
    if (plan.billing_period === 'week' || isWeeklyCode(plan.code)) {
      return this.translate.translate('membership.plan.option_week', { price, min: this.minWeeks(), max: this.maxWeeks() });
    }
    return this.translate.translate('membership.plan.option_other', {
      duration: durationLabel(this.translate, plan.code), price
    });
  }

  /** True once more than one tier is on offer — then, and only then, the plan step
   *  needs its tier headings (a single group is just the plan list it always was). */
  showTierGroups = computed(() => this.planTierGroups().length > 1);

  // ----- DNA & Health bundle (Website V2) ------------------------------------
  /**
   * The bundle this wizard pass is buying, or null for an ordinary membership. Set
   * from ?plan=GH_DNA_HEALTH… (the sales page's hand-over) — or when resuming an
   * unpaid application that already has the bundle selected — and only when the
   * server both listed it and offers it (`offered_bundle_codes`).
   */
  requestedBundle = signal<string | null>(null);
  bundleMode = computed(() => !!this.requestedBundle());
  /** The bundle's plan card, for the price and the guarantee flag. */
  bundlePlan = computed(() => this.plans().find(p => p.code === this.requestedBundle()) ?? null);
  /** Only what the server says: the plan's own `guarantee` flag (the SPECIAL has
   *  one, the DNA Family SPECIAL deliberately not). No code-based fallback — a
   *  guarantee box shown for a package without one would be a false promise. */
  bundleHasGuarantee = computed(() => !!this.bundlePlan()?.guarantee);
  /** The DNA Family SPECIAL ($999, invitation only, no voluntary money-back guarantee). */
  familyMode = computed(() => isFamilyCode(this.requestedBundle()) || !!this.bundlePlan()?.family);
  /** The `gh_product.<key>` node of the bundle being bought. */
  bundleProductKey = computed(() => this.familyMode() ? 'dna_family_special' : 'dna_health_special');
  /** Payment step: the required "I understand: no voluntary money-back guarantee" box. */
  familyTermsAck = signal(false);

  isBundleCode(code: string | null | undefined): boolean {
    return !!code && (BUNDLE_PLAN_CODES.includes(code) || FAMILY_BUNDLE_PLAN_CODES.includes(code));
  }

  /**
   * Which bundle to sell, given the ?plan= preselect and the loaded application.
   * A bundle that is not listed or not offered falls back to the ordinary plan step
   * (with a note) instead of a dead card — the server would refuse it in set_plan.
   */
  private resolveBundle(preselect: string | null, app: MembershipApplication): string | null {
    const candidate = this.isBundleCode(preselect)
      ? preselect
      : (this.isBundleCode(app.selected_plan) && app.membership_status === 'PENDING_APPLICATION'
          ? app.selected_plan ?? null
          : null);
    if (!candidate) return null;

    const listed = app.plans.some(p => p.code === candidate);
    // The family bundle is offered only with an accepted invitation, which the
    // server reports in its own list — fail closed when it is absent.
    const offered = isFamilyCode(candidate)
      ? (app.offered_family_codes ?? []).includes(candidate)
      : (app.offered_bundle_codes ?? [candidate]).includes(candidate);
    if (listed && offered) return candidate;

    if (this.isBundleCode(preselect)) {
      this.error.set(this.translate.translate(
        isFamilyCode(preselect) ? 'membership.family.unavailable' : 'membership.bundle.unavailable'));
    }
    return null;
  }

  // ----- Practitioner Network upgrade --------------------------------------
  /** True when this wizard pass is buying the upgrade, not a membership. */
  upgradeMode = signal(false);
  /** The server's difference-price offer (package + price), or null. */
  upgradeOffer = computed<UpgradeOffer | null>(() => this.app()?.upgrade_offer ?? null);
  /** Single required acknowledgement: the upgrade starts at once, no cooling-off. */
  upgradeWaiver = signal(false);
  /** What the upgrade costs — the server's total, never recomputed here. */
  upgradeTotal = computed(() => Number(this.upgradeOffer()?.total_price ?? 0));

  // ----- Upgrade to the DNA & Health SPECIAL (decision D of 2026-10-03) ------
  /** True when this wizard pass buys the $2,099 SPECIAL upgrade. */
  specialUpgradeMode = signal(false);
  /** The server's offer (price, credit, guarantee), or null — never decided here. */
  specialUpgradeOffer = computed<SpecialUpgradeOffer | null>(() => this.app()?.special_upgrade_offer ?? null);
  specialUpgradeTotal = computed(() => Number(this.specialUpgradeOffer()?.total_price ?? 0));
  /** The five components of the SPECIAL, as the dashboard lists them. */
  readonly specialComponents = ['dna', 'nutrition', 'fitness', 'dr_future', 'membership'];

  // ----- Practitioner Membership on a running SPECIAL (decision 3 of 2026-10-04) --
  /** True when this wizard pass buys the Practitioner Membership on the SPECIAL. */
  practitionerRenewalMode = signal(false);
  /** The server's offer (rhythms + prices, retention end, base date), or null. */
  practitionerRenewalOffer = computed<PractitionerRenewalOffer | null>(() =>
    this.app()?.practitioner_renewal_offer ?? null);
  /** The rhythm chosen on the renewal step. */
  renewalPlan = signal<string | null>(null);
  renewalPlanInfo = computed(() =>
    this.practitionerRenewalOffer()?.plans.find(p => p.code === this.renewalPlan()) ?? null);
  renewalWeekly = computed(() => this.renewalPlanInfo()?.billing_period === 'week');
  renewalTotal = computed(() => {
    const plan = this.renewalPlanInfo();
    if (!plan) return 0;
    return Number(plan.unit_price) * (this.renewalWeekly() ? this.weeks() : 1);
  });
  /** The new end of the Practitioner status — DISPLAY ONLY, the server computes its own. */
  renewalNewEnd = computed(() => {
    const offer = this.practitionerRenewalOffer();
    if (!offer || !this.renewalPlanInfo()) return '';
    const end = new Date(offer.base_date);
    if (Number.isNaN(end.getTime())) return '';
    if (this.renewalWeekly()) end.setDate(end.getDate() + this.weeks() * 7);
    else end.setFullYear(end.getFullYear() + 1);
    return this.formatDate(end);
  });

  /** "19.99 USD" in the offer's currency. */
  renewalAmount(value: number | string | null | undefined): string {
    const amount = new Intl.NumberFormat(this.i18n.getLanguage() || 'en', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(Number(value ?? 0));
    return `${amount} ${this.practitionerRenewalOffer()?.plans[0]?.currency || 'USD'}`;
  }

  // ----- Weekly rhythm: prepaid weeks ---------------------------------------
  // The weekly plan is the only one whose term the customer chooses (1..39 weeks,
  // bought as order quantity). The bounds come from the backend so the product
  // rule lives in exactly one place.
  weeks    = signal<number>(1);
  minWeeks = signal<number>(1);
  maxWeeks = signal<number>(39);

  /** The plan currently selected, if it is on the loaded plan list. */
  selectedPlanInfo = computed(() => this.plans().find(p => p.code === this.selectedPlan()) ?? null);

  /** True when the selected plan is billed per week (-> show the week picker). */
  isWeeklyPlan = computed(() => this.selectedPlanInfo()?.billing_period === 'week');

  /** Selectable week counts, for the dropdown. */
  weekOptions = computed(() => {
    const out: number[] = [];
    for (let w = this.minWeeks(); w <= this.maxWeeks(); w++) out.push(w);
    return out;
  });

  /** What the weekly plan costs for the chosen number of weeks. */
  weeklyTotal = computed(() => {
    const plan = this.selectedPlanInfo();
    if (!plan) return 0;
    return Number(plan.price) * this.weeks();
  });

  /** The amount this checkout will charge: the plan price x prepaid weeks — or, in
   *  upgrade mode, the server's upgrade total (so the e-wallet check is right too). */
  dueTotal = computed(() => {
    if (this.upgradeMode()) return this.upgradeTotal();
    if (this.specialUpgradeMode()) return this.specialUpgradeTotal();
    if (this.practitionerRenewalMode()) return this.renewalTotal();
    const plan = this.selectedPlanInfo();
    if (!plan) return 0;
    return Number(plan.price) * (this.isWeeklyPlan() ? this.weeks() : 1);
  });

  // ----- "this is added to what you already hold" ----------------------------
  // A purchase on a membership whose coverage is still running does not replace it:
  // the backend stacks the new term on `paid_through` (HealthMembershipRenewalService),
  // which is the whole compensation for a member who still had unpaid weeks. The
  // wizard has to say so before the money moves, or 20 weeks on top of 6 looks like
  // paying for those 6 a second time.

  /**
   * The date this purchase would be added to, or null when nothing is being extended
   * (a first purchase, a lapsed membership that restarts today, or the upgrade — which
   * buys no term at all). `renewal_base_date` is the server's own figure: the note must
   * name the date the backend will actually compute from.
   */
  extensionBase = computed<Date | null>(() => {
    const app = this.app();
    // A SPECIAL (bought outright or as the upgrade) starts its 12 months fresh at the
    // payment (decision D of 2026-10-03) — it is not added on top of anything.
    if (this.upgradeMode() || this.specialUpgradeMode() || this.practitionerRenewalMode() ||
        this.isBundleCode(this.selectedPlan()) ||
        !app?.renewable || !app.renewal_base_date) return null;
    const base = new Date(app.renewal_base_date);
    if (Number.isNaN(base.getTime()) || base.getTime() <= Date.now()) return null;
    return base;
  });

  /** Weekly wording names the week count; the yearly one has nothing to count. */
  extensionNoteKey = computed(() =>
    this.isWeeklyPlan() ? 'membership.renewal.extends_weeks' : 'membership.renewal.extends_year'
  );

  /** The current end date, localised. */
  extensionUntil = computed(() => {
    const base = this.extensionBase();
    return base ? this.formatDate(base) : '';
  });

  /**
   * The end date this purchase leads to. DISPLAY ONLY — the server writes its own,
   * from the same base date and the same term, so this is the member's preview of
   * that arithmetic and never an input to it.
   */
  extensionNewEnd = computed(() => {
    const base = this.extensionBase();
    if (!base) return '';
    const end = new Date(base.getTime());
    if (this.isWeeklyPlan()) end.setDate(end.getDate() + this.weeks() * 7);
    else end.setFullYear(end.getFullYear() + 1);
    return this.formatDate(end);
  });

  /**
   * Shown when a Health Club member is buying the Practitioner Network at full price:
   * the tier changes today, and the club rhythms stay out of reach until the
   * Practitioner term has run out (the backend's `offered_plan_codes` enforces that,
   * so it is a fact about the purchase, not a warning about this screen).
   */
  showPractitionerSwitchNote = computed(() =>
    !this.upgradeMode() &&
    // The backend's `tier` is what was PAID for, not what is selected, so it still
    // says `club` while this very purchase is being made.
    (this.app()?.tier ?? 'club') === 'club' &&
    PRACTITIONER_PLAN_CODES.includes(this.selectedPlan() ?? '')
  );

  /** Dates in the member's own locale, like the dashboard's membership card. */
  formatDate(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(this.i18n.getLanguage() || 'en',
                                   { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  /** The member's e-wallet balance, as reported with the application. */
  ewalletBalance  = signal<number>(0);
  ewalletCurrency = signal<string>('USDT');

  /**
   * The e-wallet cannot pay part of an order: starting one it cannot cover only
   * strands the order in "awaiting confirmation" (the backend refuses it — see
   * the checkout endpoint), so the option is offered greyed out with the reason
   * rather than silently failing after the customer has committed.
   */
  ewalletSufficient = computed(() => this.ewalletBalance() >= this.dueTotal());

  methodDisabled(method: string): boolean {
    return method === 'ewallet' && !this.ewalletSufficient();
  }

  /**
   * An already-started payment that a different method cannot simply replace —
   * a bank transfer whose details went out, a gateway mid-capture, an on-chain
   * transaction. The customer may no longer remember starting it, so the wizard
   * names it and offers both ways out instead of just refusing the new checkout.
   */
  blockingOrder = signal<PendingOrder | null>(null);

  setWeeks(value: number | string): void {
    const weeks = Number(value);
    if (!Number.isFinite(weeks)) return;
    this.weeks.set(Math.min(this.maxWeeks(), Math.max(this.minWeeks(), Math.round(weeks))));
  }

  // Customer info form model (plain object, two-way bound).
  info: MembershipCustomerInfo = { billing_same_as_residential: true };

  // Country + state dropdowns (same /countries source as KYC/profile).
  countries = signal<CountryOption[]>([]);

  // Gateways currently live for Global Health. Each goes live independently once
  // its prerequisites are met (PayPal/bank credentials per frontend; web3 as soon
  // as the backend has its BSC receiving wallet set). Extend this + the backend
  // gate (payment_available?) when enabling more methods for GH.
  private readonly GH_LIVE_METHODS = ['ewallet', 'paypal', 'bank_transfer', 'web3_payment'];
  // dev + staging: the backend divides plan prices by 100 (CommissionService
  // .amount_divisor), so the plan cards show that divided amount to stay
  // consistent with the real checkout/web3 total. false in production.
  readonly paymentTestMode = environment.paymentTestMode === true;

  // Payment
  enabledMethods = signal<string[]>([]);
  // Enabled methods intersected with the gateways that are live for GH.
  liveMethods = computed(() => this.enabledMethods().filter(m => this.GH_LIVE_METHODS.includes(m)));
  paymentMethod  = signal<string>('ewallet');
  startMode      = signal<StartMode>('standard');
  orderId        = signal<number | null>(null);
  totalPrice     = signal<number>(0);
  paymentCosts   = signal<number>(0);
  totalWithCosts = signal<number>(0);
  recipientAddress = signal<string>('');

  // Immediate-activation waiver (3 required acknowledgements, PDF Step 11).
  waiver1 = signal(false);
  waiver2 = signal(false);
  waiver3 = signal(false);
  waiverAccepted = () => this.waiver1() && this.waiver2() && this.waiver3();
  canPay = () =>
    !this.busy() &&
    !this.methodDisabled(this.paymentMethod()) &&
    // The upgrade has no activation choice: it takes effect at once, and its one
    // waiver was already ticked on the upgrade step to get here.
    (this.upgradeMode() || this.startMode() === 'standard' || this.waiverAccepted()) &&
    // DNA Family SPECIAL: no purchase without the no-guarantee acknowledgement
    // (the server refuses it too: 422 family_terms_required).
    (!this.familyCheckout() || this.familyTermsAck());

  /** A plain checkout (not one of the upgrade/renewal modes) of the family bundle. */
  familyCheckout = () =>
    this.familyMode() && this.bundleMode() &&
    !this.upgradeMode() && !this.specialUpgradeMode() && !this.practitionerRenewalMode();

  // Health Declaration (English only)
  declQuestions = signal<HealthDeclarationQuestion[]>([]);
  declAnswers: Record<string, string> = {};
  ack1 = signal(false); // truthful
  ack2 = signal(false); // false info understood
  ack3 = signal(false); // underwriting authorisation
  underwritingStatus = signal<string | null>(null);

  declarationComplete = () =>
    this.declQuestions().length > 0 &&
    this.declQuestions().every(q => this.validDeclAnswer(q)) &&
    this.ack1() && this.ack2() && this.ack3();

  private validDeclAnswer(q: HealthDeclarationQuestion): boolean {
    const v = this.declAnswers[q.key];
    if (q.type === 'yes_no_na') return v === 'yes' || v === 'no' || v === 'not_applicable';
    return v === 'yes' || v === 'no';
  }

  planKey(code: string): string {
    return PLAN_KEY_BY_CODE[code] ?? code.toLowerCase();
  }

  /** The buyer-facing product name for a code (the upgrade step's from -> to line):
   *  "WolfPack Global Health Membership", never the internal product name. */
  planName(code: string | null | undefined): string {
    return productName(this.translate, code);
  }

  /** A plan's tier. The backend sends it; the code list is the fallback for a
   *  backend that predates the Practitioner Network. */
  planTier(plan: QuizPlan): MembershipTier {
    return plan.tier ?? (PRACTITIONER_PLAN_CODES.includes(plan.code) ? 'practitioner' : 'club');
  }

  /**
   * True when buying the Health Club slot, which has no health declaration — the
   * wizard skips the notice + declaration steps and goes straight from customer
   * info to payment. Keyed off the slot being bought (not requires_underwriting),
   * so an as-yet-unselected protection application is still treated as underwritten.
   */
  isClub(): boolean {
    return this.category() === 'club';
  }

  /** Where "Back" from the payment step returns to (Club has no declaration). */
  paymentBackStep(): Step {
    if (this.upgradeMode()) return 'upgrade';
    if (this.specialUpgradeMode()) return 'special_upgrade';
    if (this.practitionerRenewalMode()) return 'practitioner_renewal';
    return this.isClub() ? 'info' : 'declaration';
  }

  constructor() {
    // Tracking Engine V1: one checkout_step 'view' per step the contract knows.
    // The payment step is two steps there: method choice, then the confirmation
    // of the created order.
    effect(() => {
      if (this.loading()) return;
      const step = this.step();
      const hasOrder = this.orderId() !== null;
      const key = `${step}/${hasOrder}`;
      if (key === this.lastTrackedView) return;
      this.lastTrackedView = key;
      const tracked: TrackingCheckoutStep | null = step === 'plan' ? 'plan'
        : step === 'payment' ? (hasOrder ? 'payment_confirmation' : 'payment_method')
        : step === 'success' ? 'success' : null;
      if (tracked) this.tracking.checkoutStep(TRACK_PAGE, tracked, 'view');
    });

    // Never leave a method the customer cannot use selected — the balance is
    // only known once the application has loaded, which is after the default
    // method was picked.
    effect(() => {
      if (!this.methodDisabled(this.paymentMethod())) return;
      const alternative = this.liveMethods().find(m => !this.methodDisabled(m));
      if (alternative) this.paymentMethod.set(alternative);
    });

    // Signed in from the direct checkout's "you already have an account": the
    // purchase has been reached, so the member's own home URL comes back.
    consumeLoginReturn();

    const preselect = this.route.snapshot.queryParamMap.get('plan');
    // ?plan= (website, direct checkout, a dashboard plan card) means the plan was
    // already chosen: the plan step is skipped, with "change" on the summary.
    // ?tier= (dashboard tier CTAs) only preselects the first option of that product.
    const tierParam = this.route.snapshot.queryParamMap.get('tier');
    // ?upgrade=1 (dashboard upgrade CTA) asks for the Practitioner Network upgrade.
    // It is only a request: whether there is anything to upgrade is the server's
    // call, so without an `upgrade_offer` we silently fall back to the normal entry.
    const upgradeRequested = this.route.snapshot.queryParamMap.get('upgrade') === '1';
    // ?practitioner_renewal=1 (dashboard card on a running SPECIAL): only with a
    // server-issued practitioner_renewal_offer, otherwise the normal entry.
    const renewalRequested = this.route.snapshot.queryParamMap.get('practitioner_renewal') === '1';

    // The slot to buy comes from ?category= (dashboard "add" CTAs) or, failing
    // that, is derived from ?plan=. Defaults to the club slot: the protection
    // plans are deactivated, so it is the only one with anything on sale.
    const catParam = this.route.snapshot.queryParamMap.get('category');
    const category: MembershipCategory =
      catParam === 'club' || catParam === 'protection'
        ? catParam
        : preselect && !CLUB_PLAN_CODES.includes(preselect) ? 'protection' : 'club';
    this.category.set(category);

    // ?weeks= (Website V2 membership modal, carried through the magic link): the
    // number of prepaid weeks to preselect for the weekly rhythm.
    const weeksParam = Number.parseInt(this.route.snapshot.queryParamMap.get('weeks') ?? '', 10);

    // A bundle is listed by the backend only when asked for by code.
    this.funnel.createApplication(category, this.isBundleCode(preselect) ? preselect : null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: app => {
          this.loading.set(false);
          // Before applyApp: it drops a bundle selection outside bundle mode.
          this.requestedBundle.set(this.resolveBundle(preselect, app));
          this.applyApp(app);
          // Only preselect a plan that belongs to the slot being bought.
          if (preselect && app.plans.some(p => p.code === preselect && p.category === category) &&
              (!this.isBundleCode(preselect) || this.bundleMode())) {
            this.selectedPlan.set(preselect);
          }
          if (this.bundleMode()) this.selectedPlan.set(this.requestedBundle());
          if (!preselect && (tierParam === 'club' || tierParam === 'practitioner')) {
            const first = this.planTierGroups().find(g => g.tier === tierParam)?.plans[0];
            if (first) this.selectedPlan.set(first.code);
          }
          // Only for the weekly rhythm, and only within the server's bounds.
          if (this.isWeeklyPlan() && Number.isInteger(weeksParam)) this.setWeeks(weeksParam);
          if (upgradeRequested && app.upgrade_offer) {
            this.enterUpgradeMode();
            return;
          }
          // ?plan=GH_DNA_HEALTH_SPECIAL_UPGRADE (dashboard CTA): only with a
          // server-issued offer; otherwise the normal entry, like ?upgrade=1.
          if (preselect === SPECIAL_UPGRADE_CODE && app.special_upgrade_offer) {
            this.enterSpecialUpgradeMode();
            return;
          }
          if (renewalRequested && app.practitioner_renewal_offer) {
            this.enterPractitionerRenewalMode(preselect);
            return;
          }
          this.routeToEntryStep(app);
          this.skipPreselectedPlanStep(preselect);
        },
        error: err => {
          this.loading.set(false);
          if (err?.status === 401) {
            const lang = this.i18n.getLanguage() || 'en';
            this.router.navigate(['/', lang, 'login']);
            return;
          }
          this.error.set(err?.error?.error || 'Could not load your application.');
        }
      });

    // Country/state lists (shared with KYC + profile).
    this.http.get<CountryOption[]>('/countries')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => this.countries.set(data ?? []),
        error: () => this.countries.set([])
      });

    // Which payment methods are enabled (POST avoids browser caching).
    this.http.post<{ enabled_methods: string[] }>('/config/payment_methods', {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: cfg => {
          const methods = cfg?.enabled_methods ?? [];
          this.enabledMethods.set(methods);
          // Default the selected method to the first gateway that's live for GH.
          const live = methods.filter(m => this.GH_LIVE_METHODS.includes(m));
          if (live.length && !live.includes(this.paymentMethod())) {
            this.paymentMethod.set(live[0]);
          }
        },
        error: () => this.enabledMethods.set([])
      });
  }

  private applyApp(app: MembershipApplication): void {
    this.app.set(app);
    this.locked.set(!!app.locked);
    // Keep the underwriting status in sync so the (locked) declaration step can
    // offer "Continue to payment" even when we entered directly on payment via
    // routeToEntryStep — otherwise Back from payment dead-ends on the declaration.
    this.underwritingStatus.set(app.underwriting_status ?? null);
    this.plans.set(app.plans);
    this.selectedPlan.set(app.selected_plan ?? app.recommended_plan ?? null);
    // A bundle already on the row (e.g. a purchased bundle membership being renewed)
    // must not silently become the renewal: outside bundle mode the member picks a
    // membership rhythm instead.
    if (this.isBundleCode(this.selectedPlan()) && !this.bundleMode()) this.selectedPlan.set(null);
    if (app.ewallet_balance !== undefined && app.ewallet_balance !== null) {
      this.ewalletBalance.set(Number(app.ewallet_balance));
    }
    if (app.ewallet_currency) this.ewalletCurrency.set(app.ewallet_currency);
    if (app.min_weeks) this.minWeeks.set(app.min_weeks);
    if (app.max_weeks) this.maxWeeks.set(app.max_weeks);
    // Restore a term the customer already committed to, so leaving and coming
    // back cannot quietly re-price a pending order down to a single week.
    if (app.term_weeks) this.weeks.set(app.term_weeks);
    // Restore the activation choice (standard/immediate) the customer made at
    // checkout so it isn't reset when they leave and come back.
    if (app.start_mode === 'standard' || app.start_mode === 'immediate') {
      this.startMode.set(app.start_mode);
    }
    this.info = {
      ...this.info,
      // Blank server values are dropped: the backend prefills what it knows and
      // leaves the rest null, which must not clear what the customer typed.
      ...this.filled(app.customer_info),
      billing_same_as_residential: app.customer_info?.billing_same_as_residential ?? true
    };
  }

  /** The entries of `info` that actually carry a value. */
  private filled(info?: MembershipCustomerInfo | null): Partial<MembershipCustomerInfo> {
    if (!info) return {};
    return Object.fromEntries(
      Object.entries(info).filter(([, value]) => value !== null && value !== undefined && value !== '')
    );
  }

  /**
   * Pick the wizard's entry step from the application state so a finalized
   * application can't be edited again (WPGH rule: once the declaration is
   * approved/submitted the customer may only continue to payment):
   *   - renewable membership (active/expired) -> plan step, to extend it
   *   - terminal membership (cancelled/declined) -> dashboard (nothing to do here)
   *   - underwriting APPROVED        -> payment
   *   - declaration submitted (not approved) -> read-only declaration + status
   *   - otherwise (in progress)      -> normal flow (plan)
   */
  private routeToEntryStep(app: MembershipApplication): void {
    if (app.membership_status && app.membership_status !== 'PENDING_APPLICATION') {
      // A renewable membership is precisely what this wizard is for now: a further
      // purchase EXTENDS it instead of starting a second one. Only terminal states
      // (cancelled/declined) genuinely have nothing to do here.
      if (!app.renewable) {
        this.goToDashboard();
        return;
      }
      // A renewal always starts at the plan step — the member picks the rhythm and
      // the number of weeks again. Resuming at payment would carry over the term of
      // the purchase they already paid for.
      this.step.set('plan');
      return;
    }
    // Resume an outstanding bank transfer straight onto its details/reference,
    // rather than back at the payment-method choice. The confirmation component
    // re-fetches the beneficiary details (idempotent) from the existing order.
    if (this.resumeBankTransfer(app)) return;
    // Any other open payment that a method change cannot replace (a gateway
    // mid-capture, an on-chain transaction): show it up front on the payment
    // step, so the customer meets it before picking a method rather than after.
    if (app.pending_order?.blocks_method_change) {
      this.blockingOrder.set(app.pending_order);
      this.step.set('payment');
      return;
    }
    // Club slot has no declaration, but the details step is still always walked:
    // the customer has to see and confirm the address/birthday the order is
    // placed with, even when it was prefilled from their profile. Start at plan
    // selection.
    if (this.isClub()) return;
    if (app.underwriting_status === 'APPROVED') { this.step.set('payment'); return; }
    if (app.underwriting_status) { this.loadDeclarationThenShow(); return; }
    // else: in progress — leave the default 'plan' step.
  }

  /**
   * If the application has an outstanding bank transfer (order started, not yet
   * booked), jump straight to the payment step with its confirmation showing.
   * Returns true when it took over routing.
   */
  private resumeBankTransfer(app: MembershipApplication): boolean {
    const po = app.pending_order;
    if (!po || po.payment_method !== 'bank_transfer' || po.status === 'completed') return false;

    this.paymentMethod.set('bank_transfer');
    this.totalPrice.set(Number(po.total_price));
    this.totalWithCosts.set(Number(po.total_with_costs));
    this.paymentCosts.set(Number(po.total_with_costs) - Number(po.total_price));
    this.orderId.set(po.order_id);
    this.step.set('payment');
    return true;
  }

  private loadDeclarationThenShow(): void {
    this.funnel.getDeclaration(this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: d => {
          this.declQuestions.set(d.questions);
          this.declAnswers = { ...this.declAnswers, ...d.answers };
          this.underwritingStatus.set(d.underwriting_status ?? null);
          this.step.set('declaration');
        },
        error: () => this.step.set('declaration')
      });
  }

  /**
   * A plan that arrived preselected and is on offer is confirmed right away (the
   * server records it, as "Continue" would) and the wizard opens on the details.
   * Only from the plan step — every other entry step (a bank transfer to finish, a
   * blocked payment, the upgrade) keeps its own routing.
   */
  private skipPreselectedPlanStep(preselect: string | null): void {
    if (!preselect || this.step() !== 'plan' || this.upgradeMode() || this.practitionerRenewalMode() || this.locked()) return;
    if (this.selectedPlan() !== preselect) return;
    const app = this.app();
    // A terminal membership is sent to the dashboard by routeToEntryStep.
    if (!app || (app.membership_status && app.membership_status !== 'PENDING_APPLICATION' && !app.renewable)) return;
    const offered = this.bundleMode() || this.visiblePlans().some(p => p.code === preselect);
    if (!offered) return;
    this.planStepSkipped.set(true);
    this.confirmPlan();
  }

  /** "Change" on the summary: back to the plan step with its duration options. */
  changePlan(): void {
    this.error.set(null);
    this.step.set('plan');
  }

  // ----- Step: plan -------------------------------------------------------

  choosePlan(code: string): void { this.selectedPlan.set(code); }

  planDiffersFromRecommendation(): boolean {
    const app = this.app();
    return !!app?.recommended_plan && this.selectedPlan() !== app.recommended_plan;
  }

  confirmPlan(): void {
    const plan = this.selectedPlan();
    if (!plan) return;
    this.busy.set(true);
    this.error.set(null);
    this.tracking.checkoutStep(TRACK_PAGE, 'plan', 'submit');
    this.funnel.setApplicationPlan(plan, this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: app => { this.busy.set(false); this.applyApp(app); this.step.set(this.stepAfterPlan(app)); },
        error: err => {
          this.busy.set(false);
          this.tracking.checkoutStep(TRACK_PAGE, 'plan', 'error', trackingErrorCategory(err));
          this.error.set(err?.error?.error || 'Could not save your plan.');
        }
      });
  }

  /**
   * Where to go after (re-)confirming a plan. Always the details step — details
   * carried over from the profile or an earlier application are a prefill, not a
   * confirmation, so the customer still has to see and submit them. The one
   * exception is an underwritten plan whose declaration is already APPROVED:
   * those details are locked and may only be continued to payment.
   */
  private stepAfterPlan(app: MembershipApplication): Step {
    if (this.isClub()) return 'info';
    return app.underwriting_status === 'APPROVED' ? 'payment' : 'info';
  }

  // ----- Step: customer info ---------------------------------------------

  /** States/provinces for the selected residential country (empty if none). */
  statesForCountry(): { code: string; name: string }[] {
    const country = this.countries().find(c => c.code === this.info.res_country);
    return (country?.states ?? []).filter(s => !!s.name);
  }

  /** Reset the dependent state when the country changes. */
  onCountryChange(): void { this.info.res_state = null; }

  /** States/provinces for the selected billing country (empty if none). */
  statesForBillingCountry(): { code: string; name: string }[] {
    const country = this.countries().find(c => c.code === this.info.bill_country);
    return (country?.states ?? []).filter(s => !!s.name);
  }

  /** Reset the dependent billing state when the billing country changes. */
  onBillingCountryChange(): void { this.info.bill_state = null; }

  infoValid(): boolean {
    const residentialValid = !!(this.info.first_name && this.info.last_name &&
      this.info.date_of_birth && this.info.phone &&
      this.info.res_address && this.info.res_city &&
      this.info.res_postal_code && this.info.res_country);
    if (!residentialValid) return false;
    if (!this.info.billing_same_as_residential) {
      return !!(this.info.bill_address && this.info.bill_city &&
        this.info.bill_postal_code && this.info.bill_country);
    }
    return true;
  }

  submitInfo(): void {
    if (!this.infoValid()) return;
    this.busy.set(true);
    this.error.set(null);
    this.funnel.setCustomerInfo(this.info, this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: app => {
          this.busy.set(false);
          this.applyApp(app);
          // Club slot skips the language notice + health declaration entirely.
          if (this.isClub()) { this.step.set('payment'); }
          else { this.goToNotice(); }
        },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error || 'Could not save your details.'); }
      });
  }

  // ----- Step: translation notice + declaration --------------------------

  private goToNotice(): void {
    this.step.set('notice');
    // Preload the declaration questions so the next step renders instantly.
    this.funnel.getDeclaration(this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: d => {
          this.declQuestions.set(d.questions);
          this.declAnswers = { ...this.declAnswers, ...d.answers };
          this.underwritingStatus.set(d.underwriting_status ?? null);
        },
        error: () => { /* questions load again on demand */ }
      });
  }

  acceptNotice(): void {
    this.busy.set(true);
    this.error.set(null);
    this.funnel.acceptDeclarationNotice(this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => { this.busy.set(false); this.step.set('declaration'); },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error || 'Could not continue.'); }
      });
  }

  setDeclAnswer(key: string, value: string): void { this.declAnswers[key] = value; }

  /** Answer a question; clear the conditional follow-up if the answer isn't "yes". */
  answerQuestion(q: HealthDeclarationQuestion, value: string): void {
    this.declAnswers[q.key] = value;
    if (q.follow_up && value !== 'yes') delete this.declAnswers[q.follow_up.key];
  }

  submitDeclaration(): void {
    if (!this.declarationComplete()) return;
    this.busy.set(true);
    this.error.set(null);
    this.funnel.submitDeclaration(this.declAnswers, {
      ack_truthful: this.ack1(), ack_false_info: this.ack2(), ack_underwriting: this.ack3()
    }, this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.busy.set(false);
          this.underwritingStatus.set(res.underwriting_status);
          this.locked.set(true); // submitted — answers are now read-only
          this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'declaration_submitted', name: res.underwriting_status });
          if (res.checkout_allowed) this.step.set('payment');
        },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error || 'Could not submit your declaration.'); }
      });
  }

  requestReview(): void {
    this.busy.set(true);
    this.funnel.requestManualReview(this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => { this.busy.set(false); this.underwritingStatus.set(res.underwriting_status); },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error || 'Could not request a review.'); }
      });
  }

  // ----- Step: Practitioner Network upgrade -------------------------------

  /** Show the upgrade step. Everything it renders comes from `upgrade_offer`. */
  private enterUpgradeMode(): void {
    this.upgradeMode.set(true);
    // The upgrade takes effect at once — there is no standard/immediate choice, and
    // no 14-day window to start (the one being upgraded has already been served).
    this.startMode.set('immediate');
    this.step.set('upgrade');
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'upgrade_start', name: this.upgradeOffer()?.to_plan });
  }

  /**
   * Regular SPECIAL purchase by a member the server also offers the $2,099 upgrade
   * to (customer instruction 2026-10-19, rule C: both ways visible). Only before an
   * order exists — after that the payment in flight decides — and only on the
   * steps before payment.
   */
  specialUpgradeHintVisible = computed(() =>
    this.requestedBundle() === 'GH_DNA_HEALTH_SPECIAL' && !!this.specialUpgradeOffer() &&
    !this.specialUpgradeMode() && this.orderId() === null &&
    (this.step() === 'plan' || this.step() === 'info'));

  /** Leave bundle mode for the upgrade card; the upgrade's offer is the server's. */
  switchToSpecialUpgrade(): void {
    if (!this.specialUpgradeOffer()) return;
    this.error.set(null);
    this.requestedBundle.set(null);
    this.planStepSkipped.set(false);
    // A reload should land on the upgrade too, as the dashboard's upgrade CTA does.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { category: 'club', plan: SPECIAL_UPGRADE_CODE },
      replaceUrl: true
    });
    this.enterSpecialUpgradeMode();
  }

  /** Show the ONE SPECIAL-upgrade card. Everything it renders is the server's offer. */
  private enterSpecialUpgradeMode(): void {
    this.specialUpgradeMode.set(true);
    // The kit follows the 14-day rule unless the member consents to an immediate
    // start on the payment step (decision F of 2026-10-03).
    this.startMode.set('standard');
    this.step.set('special_upgrade');
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'special_upgrade_start', name: SPECIAL_UPGRADE_CODE });
  }

  /** Show the Practitioner-Membership-on-SPECIAL step (decision 3 of 2026-10-04). */
  private enterPractitionerRenewalMode(preselect: string | null): void {
    const offer = this.practitionerRenewalOffer();
    if (!offer) return;
    this.practitionerRenewalMode.set(true);
    // No activation choice: the Practitioner term is added to the status the member
    // holds (or starts at the payment) — the SPECIAL itself is untouched.
    this.startMode.set('standard');
    if (offer.min_weeks) this.minWeeks.set(offer.min_weeks);
    if (offer.max_weeks) this.maxWeeks.set(offer.max_weeks);
    const pre = offer.plans.find(p => p.code === preselect) ?? offer.plans.find(p => p.billing_period === 'year') ?? offer.plans[0];
    this.renewalPlan.set(pre?.code ?? null);
    this.step.set('practitioner_renewal');
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'practitioner_renewal_start', name: pre?.code });
  }

  chooseRenewalPlan(code: string): void { this.renewalPlan.set(code); }

  confirmPractitionerRenewal(): void {
    if (!this.renewalPlanInfo()) return;
    this.error.set(null);
    this.step.set('payment');
  }

  confirmSpecialUpgrade(): void {
    if (!this.specialUpgradeOffer()) return;
    this.error.set(null);
    this.step.set('payment');
  }

  /** "2,099.00 USDT" in the offer's own currency. */
  specialAmount(value: number | string | null | undefined): string {
    const amount = new Intl.NumberFormat(this.i18n.getLanguage() || 'en', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(Number(value ?? 0));
    return `${amount} ${this.specialUpgradeOffer()?.currency || 'USD'}`;
  }

  /** The upgrade's package line: N prepaid weeks, or the yearly term. */
  upgradePackageKey(): string {
    return this.upgradeOffer()?.billing_period === 'week'
      ? 'membership.upgrade.package_weeks'
      : 'membership.upgrade.package_year';
  }

  /** Move on to payment once the single waiver is ticked. */
  confirmUpgrade(): void {
    if (!this.upgradeWaiver() || !this.upgradeOffer()) return;
    this.error.set(null);
    this.step.set('payment');
  }

  // ----- Step: payment ----------------------------------------------------

  startCheckout(): void {
    this.busy.set(true);
    this.error.set(null);
    this.tracking.checkoutStep(TRACK_PAGE, 'payment_method', 'submit');
    // In upgrade mode the package and price are the server's (`upgrade_offer`), so
    // nothing about them is sent — only how to pay and the acknowledgement.
    // The newest referral this browser holds travels with every purchase (last
    // touch; the backend locks the sponsor only at the first confirmed payment).
    // A family purchase never carries a referral: the sponsor of a family account is
    // the inviting main account (set by the server), never a stored partner link.
    const sponsorId = this.familyCheckout() ? null : currentReferral();
    const request = this.upgradeMode()
      ? this.funnel.upgradeMembership(this.paymentMethod(), true, this.category(), sponsorId)
      : this.specialUpgradeMode()
      ? this.funnel.specialUpgradeMembership(this.paymentMethod(), this.startMode(), this.waiverAccepted(), sponsorId)
      : this.practitionerRenewalMode()
      ? this.funnel.practitionerRenewal(this.renewalPlan() ?? '', this.renewalWeekly() ? this.weeks() : null,
                                        this.paymentMethod(), sponsorId)
      : this.funnel.checkoutMembership(
          this.paymentMethod(), this.startMode(), this.waiverAccepted(), this.category(),
          this.isWeeklyPlan() ? this.weeks() : null, sponsorId,
          { familyTermsAccepted: this.familyCheckout() && this.familyTermsAck() }
        );

    request
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.onCheckoutStarted(res),
        error: err => this.onCheckoutError(err)
      });
  }

  /** Shared by the membership checkout and the upgrade — both return an order. */
  private onCheckoutStarted(res: MembershipCheckout): void {
    // A switch (or a PayPal retry) after an unfinished gateway payment lands
    // here with the same order id.
    this.gatewayFailed.set(false);
    this.orderId.set(res.order_id);
    this.totalPrice.set(Number(res.total_price));
    this.totalWithCosts.set(Number(res.total_with_costs));
    this.paymentCosts.set(Number(res.total_with_costs) - Number(res.total_price));
    this.matomo.trackEvent({
      category: MATOMO_CATEGORY,
      action: this.upgradeMode() ? 'upgrade_checkout'
        : this.specialUpgradeMode() ? 'special_upgrade_checkout'
        : this.practitionerRenewalMode() ? 'practitioner_renewal_checkout' : 'checkout',
      name: this.paymentMethod()
    });

    if (this.paymentMethod() === 'web3_payment') {
      // Web3 confirmation needs the recipient address up front.
      this.orders.initiatePayment(res.order_id, 'web3_payment')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ip => { this.recipientAddress.set(ip?.payment_details?.recipient_address || ''); this.busy.set(false); },
          // Back to method selection: the confirmation is not rendered without a
          // recipient, and staying on the order would leave the step empty.
          error: () => { this.busy.set(false); this.orderId.set(null); this.error.set('Could not initialise the on-chain payment.'); }
        });
    } else {
      this.busy.set(false);
    }
  }

  private onCheckoutError(err: any): void {
    this.busy.set(false);
    this.tracking.checkoutStep(TRACK_PAGE, 'payment_method', 'error',
      err?.error?.user_balance !== undefined ? 'payment_declined' : trackingErrorCategory(err));
    // The backend re-checks the balance at checkout (it may have been spent
    // since the page loaded). Show the same translated hint the greyed-out
    // option carries, not the raw API message.
    if (err?.error?.user_balance !== undefined) {
      this.ewalletBalance.set(Number(err.error.user_balance));
      if (err.error.currency) this.ewalletCurrency.set(err.error.currency);
      this.error.set(this.translate.translate('membership.payment.ewallet_insufficient', {
        balance: err.error.user_balance,
        total: err.error.order_total ?? this.dueTotal(),
        currency: err.error.currency ?? this.ewalletCurrency()
      }));
      return;
    }
    // 409: another payment is still open and this method cannot replace it.
    // Hand the customer the panel that names it and lets them continue or
    // cancel, rather than a refusal they can do nothing with.
    if (err?.status === 409 && err?.error?.pending_order) {
      this.blockingOrder.set(err.error.pending_order);
      return;
    }
    // DNA Family SPECIAL refusals, in plain words rather than the API text.
    const code: string | undefined = err?.error?.code ?? err?.error?.error;
    if (code === 'family_terms_required' || code === 'family_invitation_required' ||
        code === 'family_quota_exhausted') {
      if (code === 'family_terms_required') this.familyTermsAck.set(false);
      this.error.set(this.translate.translate(`membership.family.errors.${code}`));
      return;
    }
    this.error.set(err?.error?.error || 'Could not start checkout.');
  }

  /** True while the member picks another method after an unfinished PayPal payment. */
  gatewayFailed = signal(false);

  /**
   * The PayPal flow ended without a payment (window closed/cancelled, PayPal
   * error, capture declined — for Global Health a card is paid through PayPal):
   * keep the order and go back to method selection with a plain-words notice. The
   * next checkout re-points the SAME order to the chosen method (backend
   * resolve_reusable_order) — package, bundle, details and referral are untouched.
   * Another method is preselected; PayPal stays selectable for a second try.
   */
  onGatewayFailed(failure: PaypalFailure): void {
    this.gatewayFailed.set(true);
    this.tracking.checkoutStep(TRACK_PAGE, 'payment_confirmation', 'error', 'payment_declined');
    this.matomo.trackEvent({
      category: MATOMO_CATEGORY, action: 'gateway_failed', name: `${this.paymentMethod()}:${failure.reason}`
    });
    const alternative = this.liveMethods().find(m => m !== 'paypal' && !this.methodDisabled(m));
    if (alternative) this.paymentMethod.set(alternative);
    this.orderId.set(null);
  }

  onPaymentSuccess(): void {
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'payment_success', name: this.startMode() });
    this.step.set('success');
  }

  /**
   * Customer deliberately abandoned the bank transfer (warned in the component).
   * Cancel the order server-side, then restart at plan selection so a fresh order
   * is created — they must re-pick the package. The declaration/details are kept.
   */
  /** Go back to the blocked payment and finish it. */
  resumeBlockingOrder(): void {
    const order = this.blockingOrder();
    if (!order) return;

    this.blockingOrder.set(null);
    this.error.set(null);
    this.paymentMethod.set(order.payment_method);
    this.totalPrice.set(Number(order.total_price));
    this.totalWithCosts.set(Number(order.total_with_costs));
    this.paymentCosts.set(Number(order.total_with_costs) - Number(order.total_price));
    this.orderId.set(order.order_id);
    this.step.set('payment');
  }

  /**
   * Drop the blocked payment so another method can be used. Same server call as
   * the bank-transfer cancel — the customer confirms first, because for a bank
   * transfer they may already have sent the money.
   */
  cancelBlockingOrder(): void {
    const order = this.blockingOrder();
    if (!order || order.can_cancel === false) return;

    const message = order.payment_method === 'bank_transfer'
      ? this.translate.translate('membership.payment.bank_transfer_cancel_confirm')
      : this.translate.translate('membership.payment.pending_cancel_confirm');
    if (!confirm(message)) return;

    this.busy.set(true);
    this.error.set(null);
    this.funnel.cancelCheckout(this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: app => {
          this.busy.set(false);
          this.blockingOrder.set(null);
          this.applyApp(app);
          this.orderId.set(null);
          this.startMode.set('standard');
          this.waiver1.set(false); this.waiver2.set(false); this.waiver3.set(false);
          this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'pending_order_cancelled', name: order.payment_method });
          this.step.set(this.restartStep());
        },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error || 'Could not cancel the payment.'); }
      });
  }

  cancelBankTransfer(): void {
    this.busy.set(true);
    this.error.set(null);
    this.funnel.cancelCheckout(this.category())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: app => {
          this.busy.set(false);
          this.applyApp(app);
          // Reset the payment-side selections and drop back to package choice.
          this.orderId.set(null);
          this.startMode.set('standard');
          this.waiver1.set(false); this.waiver2.set(false); this.waiver3.set(false);
          this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'bank_transfer_cancelled' });
          this.step.set(this.restartStep());
        },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error || 'Could not cancel the bank transfer.'); }
      });
  }

  /**
   * Where a cancelled payment drops the customer back to. Normally the package
   * choice; in upgrade mode the upgrade step — the plan step would offer the
   * renewal plans instead, which is not what they came here for.
   */
  private restartStep(): Step {
    if (this.specialUpgradeMode() && this.specialUpgradeOffer()) return 'special_upgrade';
    if (this.practitionerRenewalMode() && this.practitionerRenewalOffer()) return 'practitioner_renewal';
    return this.upgradeMode() && this.upgradeOffer() ? 'upgrade' : 'plan';
  }

  goToDashboard(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'dashboard']);
  }

  back(to: Step): void { this.step.set(to); }
}
